/**
 * Card tree helpers: badge roll-ups (red and yellow counts over descendant
 * data items), the issue list, search filtering and ancestor paths. Pure, so
 * the Health panel and the dialogs share one definition of "an issue".
 */
import type { StatusColour, Subsystem } from '../../types';
import type { ReadingRef } from '../state';

/** Current colour of one data item (readings override the JSON); null when the status is bad data. */
export type ItemColourLookup = (subsystemId: string, dataId: string) => StatusColour | null;
export type ItemValueLookup = (subsystemId: string, dataId: string) => string;

export interface Badges {
  red: number;
  yellow: number;
}

/** Red and yellow data items in this subsystem and every descendant (the previous data-issueCount / data-warningCount bubbles). */
export function rollupBadges(subsystem: Subsystem, colourOf: ItemColourLookup): Badges {
  const badges: Badges = { red: 0, yellow: 0 };
  const visit = (s: Subsystem) => {
    for (const d of s.data ?? []) {
      const c = colourOf(s.id, d.id);
      if (c === 'red') badges.red++;
      else if (c === 'yellow') badges.yellow++;
    }
    for (const child of s.childs ?? []) visit(child);
  };
  visit(subsystem);
  return badges;
}

/** Ancestors first, the subsystem itself last; empty when the id is not in the tree. */
export function pathTo(subsystems: Subsystem[], id: string): Subsystem[] {
  const walk = (list: Subsystem[], trail: Subsystem[]): Subsystem[] | null => {
    for (const s of list) {
      const next = [...trail, s];
      if (s.id === id) return next;
      if (s.childs?.length) {
        const found = walk(s.childs, next);
        if (found) return found;
      }
    }
    return null;
  };
  return walk(subsystems, []) ?? [];
}

export function pathLabel(path: Subsystem[]): string {
  return path.map((s) => s.label).join(' > ');
}

export interface IssueItem {
  subsystemId: string;
  subsystemLabel: string;
  path: string;
  dataId: string;
  label: string;
  value: string;
  colour: 'red' | 'yellow';
}

/** Every red item, then every yellow item, each in document order, with its card path for the jump link. */
export function collectIssues(subsystems: Subsystem[], colourOf: ItemColourLookup, valueOf: ItemValueLookup): IssueItem[] {
  const red: IssueItem[] = [];
  const yellow: IssueItem[] = [];
  const visit = (s: Subsystem, trail: Subsystem[]) => {
    const path = [...trail, s];
    for (const d of s.data ?? []) {
      const c = colourOf(s.id, d.id);
      if (c !== 'red' && c !== 'yellow') continue;
      const item: IssueItem = {
        subsystemId: s.id,
        subsystemLabel: s.label,
        path: pathLabel(path),
        dataId: d.id,
        label: d.label,
        value: valueOf(s.id, d.id),
        colour: c,
      };
      (c === 'red' ? red : yellow).push(item);
    }
    for (const child of s.childs ?? []) visit(child, path);
  };
  for (const s of subsystems) visit(s, []);
  return [...red, ...yellow];
}

export interface TreeFilter {
  /** Subsystems to render (matches and their ancestors). */
  visible: Set<string>;
  /** Ancestors of matches, opened so the match is on screen. */
  expand: Set<string>;
  /** Data items that matched, so rows can be highlighted. */
  items: Set<string>;
}

/** Case-insensitive search over labels, ids, descriptions and data rows; null when the query is blank. */
export function filterTree(subsystems: Subsystem[], query: string): TreeFilter | null {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  const result: TreeFilter = { visible: new Set(), expand: new Set(), items: new Set() };
  const matches = (text: string | undefined) => (text ?? '').toLowerCase().includes(q);
  const visit = (s: Subsystem, trail: Subsystem[]): boolean => {
    const hit = matches(s.label) || matches(s.id) || matches(s.description);
    let itemHit = false;
    if (s.reference?.values.some((v) => matches(v.label) || matches(v.value))) itemHit = true;
    for (const d of s.data ?? []) {
      if (matches(d.label) || matches(d.id) || matches(d.value)) {
        result.items.add(`${s.id}.${d.id}`);
        itemHit = true;
      }
    }
    let childHit = false;
    for (const child of s.childs ?? []) if (visit(child, [...trail, s])) childHit = true;
    if (hit || itemHit || childHit) {
      result.visible.add(s.id);
      for (const t of trail) result.expand.add(t.id);
      if (childHit || itemHit) result.expand.add(s.id);
      return true;
    }
    return false;
  };
  for (const s of subsystems) visit(s, []);
  return result;
}

/** Data items that can open the Readings tab: JSON history, store history or a live series. */
export function itemsWithHistory(subsystems: Subsystem[], hasHistory: (ref: ReadingRef) => boolean): Array<ReadingRef & { label: string; subsystemLabel: string }> {
  const out: Array<ReadingRef & { label: string; subsystemLabel: string }> = [];
  const visit = (s: Subsystem) => {
    for (const d of s.data ?? []) {
      const ref = { subsystemId: s.id, dataId: d.id };
      if (hasHistory(ref)) out.push({ ...ref, label: d.label, subsystemLabel: s.label });
    }
    for (const child of s.childs ?? []) visit(child);
  };
  for (const s of subsystems) visit(s);
  return out;
}

/** The subsystem's root group (physical_components, environmental, ...). */
export function rootOf(subsystems: Subsystem[], id: string): Subsystem | undefined {
  return pathTo(subsystems, id)[0];
}
